const ZIP_LOCAL_FILE_HEADER = 0x04034b50;
const ZIP_CENTRAL_DIRECTORY_HEADER = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const ZIP_UTF8_FLAG = 0x0800;
const ZIP_VERSION = 20;
const MAX_ZIP_ENTRIES = 65_535;
const MAX_ZIP32_VALUE = 0xffff_ffff;

export interface PortableArchiveEntry {
  contents: ArrayBuffer | Blob | string | Uint8Array;
  path: string;
}

export interface PortableArchive {
  blob: Blob;
  byteLength: number;
  entryCount: number;
}

export interface PortableZipStreamEntry {
  byteLength: number;
  crc32: number;
  open: () => Promise<ReadableStream<Uint8Array>>;
  path: string;
}

export interface PortableZipStreamResult {
  byteLength: number;
  entryCount: number;
}

// The previous implementation recalculated all eight CRC polynomial steps for
// every byte. That is correct, but it makes attachment capture CPU-bound: an
// 8 MiB Trello file executes roughly 67 million JavaScript loop iterations.
// Precomputing the standard CRC-32 table reduces each byte to one lookup and
// one XOR while preserving incremental/chunked operation.
const CRC32_TABLE = new Uint32Array(256);
for (let index = 0; index < CRC32_TABLE.length; index += 1) {
  let value = index;
  for (let bit = 0; bit < 8; bit += 1) {
    value = (value >>> 1) ^ (value & 1 ? 0xedb8_8320 : 0);
  }
  CRC32_TABLE[index] = value >>> 0;
}

function validateArchivePath(path: string): void {
  const segments = path.split("/");
  if (
    path === "" ||
    path.startsWith("/") ||
    path.includes("\\") ||
    segments.some(
      (segment) => segment === "" || segment === "." || segment === "..",
    )
  ) {
    throw new RangeError("Archive entries require normalized relative paths.");
  }
}

export class Crc32 {
  private crc = 0xffff_ffff;

  update(bytes: Uint8Array): this {
    for (const byte of bytes) {
      this.crc = (this.crc >>> 8) ^ CRC32_TABLE[(this.crc ^ byte) & 0xff]!;
    }
    return this;
  }

  digest(): number {
    return (this.crc ^ 0xffff_ffff) >>> 0;
  }
}

export function crc32Bytes(bytes: Uint8Array): number {
  return new Crc32().update(bytes).digest();
}

function dosDateTime(date: Date): { date: number; time: number } {
  const year = Math.max(1980, Math.min(2107, date.getUTCFullYear()));
  return {
    date:
      ((year - 1980) << 9) |
      ((date.getUTCMonth() + 1) << 5) |
      date.getUTCDate(),
    time:
      (date.getUTCHours() << 11) |
      (date.getUTCMinutes() << 5) |
      Math.floor(date.getUTCSeconds() / 2),
  };
}

function localHeader(
  nameLength: number,
  checksum: number,
  byteLength: number,
  modified: { date: number; time: number },
): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(30);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, ZIP_LOCAL_FILE_HEADER, true);
  view.setUint16(4, ZIP_VERSION, true);
  view.setUint16(6, ZIP_UTF8_FLAG, true);
  view.setUint16(8, 0, true);
  view.setUint16(10, modified.time, true);
  view.setUint16(12, modified.date, true);
  view.setUint32(14, checksum, true);
  view.setUint32(18, byteLength, true);
  view.setUint32(22, byteLength, true);
  view.setUint16(26, nameLength, true);
  return bytes;
}

function centralHeader(
  nameLength: number,
  checksum: number,
  byteLength: number,
  localOffset: number,
  modified: { date: number; time: number },
): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(46);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, ZIP_CENTRAL_DIRECTORY_HEADER, true);
  view.setUint16(4, ZIP_VERSION, true);
  view.setUint16(6, ZIP_VERSION, true);
  view.setUint16(8, ZIP_UTF8_FLAG, true);
  view.setUint16(10, 0, true);
  view.setUint16(12, modified.time, true);
  view.setUint16(14, modified.date, true);
  view.setUint32(16, checksum, true);
  view.setUint32(20, byteLength, true);
  view.setUint32(24, byteLength, true);
  view.setUint16(28, nameLength, true);
  view.setUint32(42, localOffset, true);
  return bytes;
}

function endOfCentralDirectory(
  entryCount: number,
  directoryByteLength: number,
  directoryOffset: number,
): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(22);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, ZIP_END_OF_CENTRAL_DIRECTORY, true);
  view.setUint16(8, entryCount, true);
  view.setUint16(10, entryCount, true);
  view.setUint32(12, directoryByteLength, true);
  view.setUint32(16, directoryOffset, true);
  return bytes;
}

async function entryBytes(
  contents: PortableArchiveEntry["contents"],
): Promise<Uint8Array<ArrayBuffer>> {
  if (typeof contents === "string") {
    return new TextEncoder().encode(contents);
  }

  if (contents instanceof Blob) {
    return new Uint8Array(await contents.arrayBuffer());
  }

  if (contents instanceof ArrayBuffer) {
    return new Uint8Array(contents);
  }

  return new Uint8Array(contents);
}

export async function createPortableZip(
  entries: readonly PortableArchiveEntry[],
  modifiedAt: string,
): Promise<PortableArchive> {
  if (entries.length > MAX_ZIP_ENTRIES) {
    throw new RangeError("The archive contains too many entries for ZIP32.");
  }

  const timestamp = new Date(modifiedAt);
  if (Number.isNaN(timestamp.valueOf())) {
    throw new RangeError("Archive timestamp must be a valid date.");
  }

  const paths = new Set<string>();
  const modified = dosDateTime(timestamp);
  const encoder = new TextEncoder();
  const localParts: BlobPart[] = [];
  const centralParts: BlobPart[] = [];
  let localOffset = 0;
  let directoryByteLength = 0;

  for (const entry of entries) {
    validateArchivePath(entry.path);
    if (paths.has(entry.path)) {
      throw new RangeError(`Duplicate archive entry: ${entry.path}`);
    }
    paths.add(entry.path);

    const name = new Uint8Array(encoder.encode(entry.path));
    const contents = await entryBytes(entry.contents);
    const byteLength = contents.byteLength;
    if (
      name.byteLength > 65_535 ||
      byteLength > MAX_ZIP32_VALUE ||
      localOffset > MAX_ZIP32_VALUE
    ) {
      throw new RangeError("An archive entry exceeds the ZIP32 limits.");
    }

    const checksum = crc32Bytes(contents);
    const local = localHeader(name.byteLength, checksum, byteLength, modified);
    const central = centralHeader(
      name.byteLength,
      checksum,
      byteLength,
      localOffset,
      modified,
    );
    localParts.push(local.buffer, name.buffer, contents.buffer);
    centralParts.push(central.buffer, name.buffer);
    localOffset += local.byteLength + name.byteLength + byteLength;
    directoryByteLength += central.byteLength + name.byteLength;
  }

  if (
    localOffset > MAX_ZIP32_VALUE ||
    directoryByteLength > MAX_ZIP32_VALUE ||
    localOffset + directoryByteLength + 22 > MAX_ZIP32_VALUE
  ) {
    throw new RangeError("The completed archive exceeds the ZIP32 limits.");
  }

  const end = endOfCentralDirectory(
    entries.length,
    directoryByteLength,
    localOffset,
  );
  const blob = new Blob([...localParts, ...centralParts, end.buffer], {
    type: "application/zip",
  });

  return { blob, byteLength: blob.size, entryCount: entries.length };
}

export function portableZipStreamByteLength(
  entries: readonly PortableZipStreamEntry[],
): number {
  if (entries.length > MAX_ZIP_ENTRIES) {
    throw new RangeError("The archive contains too many entries for ZIP32.");
  }
  const encoder = new TextEncoder();
  const paths = new Set<string>();
  let localLength = 0;
  let centralLength = 0;

  for (const entry of entries) {
    validateArchivePath(entry.path);
    if (paths.has(entry.path)) {
      throw new RangeError(`Duplicate archive entry: ${entry.path}`);
    }
    paths.add(entry.path);
    const nameLength = encoder.encode(entry.path).byteLength;
    if (
      nameLength > 65_535 ||
      entry.byteLength < 0 ||
      entry.byteLength > MAX_ZIP32_VALUE
    ) {
      throw new RangeError("A streamed archive entry exceeds ZIP32 limits.");
    }
    localLength += 30 + nameLength + entry.byteLength;
    centralLength += 46 + nameLength;
  }

  const total = localLength + centralLength + 22;
  if (total > MAX_ZIP32_VALUE) {
    throw new RangeError("The streamed archive exceeds ZIP32 limits.");
  }
  return total;
}

export async function writePortableZipStream(
  entries: readonly PortableZipStreamEntry[],
  writable: WritableStream<Uint8Array>,
  modifiedAt: string,
): Promise<PortableZipStreamResult> {
  if (entries.length > MAX_ZIP_ENTRIES) {
    throw new RangeError("The archive contains too many entries for ZIP32.");
  }
  const timestamp = new Date(modifiedAt);
  if (Number.isNaN(timestamp.valueOf())) {
    throw new RangeError("Archive timestamp must be a valid date.");
  }

  const encoder = new TextEncoder();
  const byteLength = portableZipStreamByteLength(entries);
  const modified = dosDateTime(timestamp);
  const paths = new Set<string>();
  const centralParts: Uint8Array[] = [];
  const writer = writable.getWriter();
  let localOffset = 0;
  let centralLength = 0;

  try {
    for (const entry of entries) {
      if (paths.has(entry.path)) {
        throw new RangeError(`Duplicate archive entry: ${entry.path}`);
      }
      paths.add(entry.path);
      const name = new Uint8Array(encoder.encode(entry.path));
      const local = localHeader(
        name.byteLength,
        entry.crc32,
        entry.byteLength,
        modified,
      );
      const central = centralHeader(
        name.byteLength,
        entry.crc32,
        entry.byteLength,
        localOffset,
        modified,
      );
      await writer.write(local);
      await writer.write(name);

      const reader = (await entry.open()).getReader();
      let streamed = 0;
      try {
        while (streamed < entry.byteLength) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          streamed += value.byteLength;
          if (streamed > entry.byteLength) {
            await reader.cancel();
            throw new RangeError(
              `Archive entry exceeded its declared size: ${entry.path}`,
            );
          }
          await writer.write(value);
        }
        if (streamed === entry.byteLength) {
          await reader.cancel("Archive entry declared byte count reached.");
        }
      } finally {
        reader.releaseLock();
      }
      if (streamed !== entry.byteLength) {
        throw new RangeError(
          `Archive entry did not match its declared size: ${entry.path}`,
        );
      }

      centralParts.push(central, name);
      localOffset += local.byteLength + name.byteLength + entry.byteLength;
      centralLength += central.byteLength + name.byteLength;
    }

    for (const part of centralParts) {
      await writer.write(part);
    }
    await writer.write(
      endOfCentralDirectory(entries.length, centralLength, localOffset),
    );
    await writer.close();
  } catch (error) {
    await writer.abort(error).catch(() => undefined);
    throw error;
  } finally {
    writer.releaseLock();
  }

  return { byteLength, entryCount: entries.length };
}

export function createLocalArchiveFilename(
  boardName: string,
  capturedAt: string,
): string {
  const boardSlug = boardName
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  const timestamp = capturedAt.replace(/\.\d{3}Z$/u, "Z").replaceAll(":", "-");

  return `board-preserve-${boardSlug || "trello-board"}-${timestamp}.zip`;
}
